import { useState } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import {
  EXAMPLE_NOVEL_ID_BY_LANG,
  EXAMPLE_NOVEL_OWNER_ID,
} from '@/config/example-novel-config';
import { useFirebase } from '@/firebase';
import { getThemeDefaultCategories } from '@/lib/theme-utils';
import type { Novel as PersistedNovel } from '@common/schemas/novel.schema';

export interface ExampleNovelsAdminResult {
  createdCount: number;
  existingCount: number;
  total: number;
}

const DEFAULT_TITLES_BY_LANG: Record<string, string> = {
  en: 'Example Novel (EN)',
  es: 'Novela de Ejemplo (ES)',
  ca: "Novel·la d'Exemple (CA)",
  fr: "Roman d'Exemple (FR)",
  it: 'Romanzo di Esempio (IT)',
  de: 'Beispielroman (DE)',
};

export function useExampleNovelsAdmin() {
  const { firestore } = useFirebase();
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ExampleNovelsAdminResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const ensureExampleNovelsExist = async () => {
    if (!firestore) {
      setError('Firestore is not initialized.');
      return;
    }

    setLoading(true);
    setError(null);

    let createdCount = 0;
    let existingCount = 0;

    const entries = Object.entries(EXAMPLE_NOVEL_ID_BY_LANG);
    const defaultCategories = getThemeDefaultCategories('Default');
    const defaultSettings: NonNullable<PersistedNovel['entitiesMeta']>['settings'] = {};

    try {
      for (const [lang, novelId] of entries) {
        const docRef = doc(firestore, 'users', EXAMPLE_NOVEL_OWNER_ID, 'novels', novelId);
        const snapshot = await getDoc(docRef);

        if (snapshot.exists()) {
          existingCount++;
        } else {
          const novelTitle = DEFAULT_TITLES_BY_LANG[lang] || `Example Novel (${lang.toUpperCase()})`;
          const novelData: PersistedNovel = {
            id: novelId,
            title: novelTitle,
            ownerId: EXAMPLE_NOVEL_OWNER_ID,
            status: 'draft',
            size: 'small',
            isExample: true,
            collaborators: {},
            readers: {},
            noteFolders: {},
            canonRevision: '1',
            storageLayout: {
              entities: {
                characters: { items: 'embedded', meta: 'embedded' },
                locations: { items: 'embedded', meta: 'embedded' },
                organizations: { items: 'embedded', meta: 'embedded' },
                events: { items: 'embedded', meta: 'embedded' },
                codex: { items: 'embedded', meta: 'embedded' },
                relationships: { items: 'embedded', meta: 'embedded' },
              },
              narrative: {
                scenes: { items: 'embedded', meta: 'embedded' },
                chapters: { items: 'embedded', meta: 'embedded' },
                contents: { items: 'split', meta: 'embedded' },
                arcs: { items: 'embedded', meta: 'embedded' },
                storyboards: { items: 'embedded', meta: 'embedded' },
              },
            },
            entitiesMeta: {
              summaries: {},
              settings: defaultSettings,
              codexCategories: defaultCategories,
              codexCategoryParents: {},
            },
            narrativeMeta: {
              summaries: {
                contents: {},
              },
            },
            aiSettings: {
              intelligenceTier: 'eco',
            },
          };

          await setDoc(docRef, novelData);
          createdCount++;
        }
      }

      setResult({
        createdCount,
        existingCount,
        total: entries.length,
      });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    result,
    error,
    ensureExampleNovelsExist,
  };
}
